// src/state/modules/auth.js
import apiClient from '@/api.js'
import router    from '@/router/index'

let profileTimer = null
let profileInflight = false
let profilePromise = null

export const state = {
    user: JSON.parse(localStorage.getItem('user')) || null,
    status: JSON.parse(localStorage.getItem('user')) ? { loggedIn: true } : {},
    authError: null,
    profileLoaded: false,
}

export const mutations = {
    setUserData(state, user) {
        state.user = user
        localStorage.setItem('user', JSON.stringify(user))
    },
    setProfileLoaded(state, loaded) {
        state.profileLoaded = !!loaded
    },

    loginRequest(state) {
        state.status = { loggingIn: true }
        state.authError = null
    },
    loginSuccess(state) {
        state.status = { loggedIn: true }
        state.authError = null
    },
    loginFailure(state, e) {
        state.status = {}
        state.user = null
        state.authError = e
        state.profileLoaded = false
        localStorage.removeItem('jwt')
        localStorage.removeItem('user')
    },

    registerRequest(state) {
        state.status = { registering: true }
        state.authError = null
    },
    registerSuccess(state) {
        state.status = { loggedIn: true }
        state.authError = null
    },
    registerFailure(state, e) {
        state.status = {}
        state.authError = e
    },

    logout(state) {
        state.user = null
        state.status = {}
        state.authError = null
        state.profileLoaded = false
        localStorage.removeItem('user')
        localStorage.removeItem('jwt')
    },

    clearProfileTimer() {
        if (profileTimer) {
            clearInterval(profileTimer)
            profileTimer = null
        }
    }
}

function normalizeCompany(c) {
    // приведение полей в единый стиль для фронта
    return {
        ...c,
        // флаги
        blocked:     c.blocked     ?? c.Block     ?? c.block ?? false,
        deleted:     c.deleted     ?? c.Deleted   ?? c.deletedAt != null ?? false,
        completed:   c.completed   ?? c.Completed ?? false,
        moderation:  c.moderation  ?? c.Moderation, // важное: bit из SQL часто приходит как "Moderation"
        internalError: c.internalError ?? c.InternalError ?? false,
        // опционально — единый нейминг
        name: c.name ?? c.CompanyName ?? c.companyName,
    }
}

export const actions = {
    // GET api/v1/profile (single-flight + boolean result)
    async fetchProfile({ state, commit, dispatch }, { force = false } = {}) {
        if (!localStorage.getItem('jwt')) {
            commit('logout')
            commit('clearProfileTimer')
            await dispatch('permissions/clearMap', null, { root: true })
            return false
        }

        if (!force && state.profileLoaded && state.user) {
            return true
        }

        if (profileInflight && profilePromise) return profilePromise

        profileInflight = true
        profilePromise = (async () => {
            try {
                const { data } = await apiClient.get('api/v1/profile')
                const { status, result } = data || {}

                if (status === 'success' && result?.user && Array.isArray(result.companies)) {
                    commit('setUserData', result.user)

                    const normalizedCompanies = result.companies.map(normalizeCompany)
                    await dispatch('company/setCompanies', normalizedCompanies, { root: true })

                    const stored = localStorage.getItem('company')
                    const targetCompany =
                        normalizedCompanies.find(c => c.CompanyUUID === stored) ||
                        normalizedCompanies[0]

                    if (targetCompany) {
                        await dispatch('company/switchCompany', targetCompany.CompanyUUID, { root: true })

                        const currentRouteName = router.currentRoute.value?.name

                        // 🛑 ПРОВЕРКА СТАТУСОВ КОМПАНИИ (в приоритете)
                        if (targetCompany.deleted) {
                            if (currentRouteName !== 'ErrorDeleted') {
                                router.push({ name: 'ErrorDeleted' }).catch(() => {})
                            }
                        } else if (targetCompany.blocked) {
                            if (currentRouteName !== 'ErrorBlocked') {
                                router.push({ name: 'ErrorBlocked' }).catch(() => {})
                            }
                        } else if (targetCompany.moderation !== true) {
                            // ⏳ модерация: false/0/undefined => ErrorModeration
                            if (currentRouteName !== 'ErrorModeration') {
                                router.push({ name: 'ErrorModeration' }).catch(() => {})
                            }
                        } else {
                            // если всё ОК, но юзер застрял на ошибке — вернуть в строй
                            const errorRoutes = ['ErrorDeleted', 'ErrorBlocked', 'ErrorModeration']
                            if (errorRoutes.includes(currentRouteName)) {
                                router.push({ name: 'dashboard' }).catch(() => {})
                            }
                        }
                    }

                    await dispatch('permissions/fetchPermissionsMap', null, { root: true })
                    commit('setProfileLoaded', true)

                    if (!profileTimer) {
                        profileTimer = setInterval(() => {
                            if (!profileInflight) {
                                dispatch('fetchProfile', { force: true }).catch(() => {
                                    commit('clearProfileTimer')
                                })
                            }
                        }, 25000)
                    }

                    return true
                } else {
                    commit('logout')
                    commit('clearProfileTimer')
                    await dispatch('permissions/clearMap', null, { root: true })
                    return false
                }
            } catch {
                commit('clearProfileTimer')
                commit('logout')
                await dispatch('permissions/clearMap', null, { root: true })
                return false
            } finally {
                profileInflight = false
                profilePromise = null
            }
        })()

        return profilePromise
    },

    // POST api/v1/auth
    async login({ dispatch, commit, state }, { username, password }) {
        commit('loginRequest')
        await dispatch('permissions/clearMap', null, { root: true })

        try {
            const { data } = await apiClient.post('api/v1/auth', { username, password })
            const { status, result } = data || {}

            if (status === 'success' && result?.token) {
                localStorage.setItem('jwt', result.token)
                commit('loginSuccess')
                commit('setUserData', result.user)
                commit('setProfileLoaded', false)

                const ok = await dispatch('fetchProfile', { force: true })
                if (!ok) {
                    commit('logout')
                    return router.push({ name: 'login' })
                }

                const userFromResult = (result && result.user) || {}
                const user = Object.keys(userFromResult).length ? userFromResult : (state.user || {})
                const rawUserType = user.UserType ?? user.userType ?? '0'
                const userTypeNum = Number(rawUserType)
                const isManagerOrAdmin = (userTypeNum === 1 || userTypeNum === 2)

                const redirectFrom = router.currentRoute.value.query.redirectFrom
                let redirect

                const activeCompanyId = localStorage.getItem('company')
                const companies = JSON.parse(localStorage.getItem('companies') || '[]')
                const activeCompanyRaw = companies.find(c => c.CompanyUUID === activeCompanyId)
                const activeCompany = activeCompanyRaw ? normalizeCompany(activeCompanyRaw) : null

                // 🛑 РЕДИРЕКТЫ ПРИ ЛОГИНЕ
                if (activeCompany?.deleted) {
                    redirect = { name: 'ErrorDeleted' }
                } else if (activeCompany?.blocked) {
                    redirect = { name: 'ErrorBlocked' }
                } else if (activeCompany?.moderation !== true) {
                    redirect = { name: 'ErrorModeration' }
                } else if (redirectFrom) {
                    redirect = redirectFrom
                } else {
                    redirect = isManagerOrAdmin ? { name: 'ManagerDashboard' } : { name: 'dashboard' }
                }

                return router.push(redirect)
            } else {
                const msg = data?.message || 'Ошибка входа'
                commit('loginFailure', msg)
                throw new Error(msg)
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Ошибка входа'
            commit('loginFailure', msg)
            await dispatch('permissions/clearMap', null, { root: true })
            throw err
        }
    },

    // POST api/v1/auth/register
    async register({ dispatch, commit }, { username, email, password }) {
        commit('registerRequest')
        await dispatch('permissions/clearMap', null, { root: true })

        try {
            const { data } = await apiClient.post('api/v1/auth/register', { username, email, password })
            const { status, result } = data || {}

            if (status === 'success' && result?.token) {
                localStorage.setItem('jwt', result.token)
                commit('registerSuccess')
                commit('setUserData', result.user)
                commit('setProfileLoaded', false)

                const ok = await dispatch('fetchProfile', { force: true })
                if (!ok) {
                    commit('logout')
                    return router.push({ name: 'login' })
                }

                // fetchProfile сам сделает редирект на ErrorModeration при необходимости
                return router.push({ name: 'dashboard' })
            } else {
                const msg = data?.message || 'Ошибка регистрации'
                commit('registerFailure', msg)
                throw new Error(msg)
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Ошибка регистрации'
            commit('registerFailure', msg)
            await dispatch('permissions/clearMap', null, { root: true })
            throw err
        }
    },

    async logout({ commit, dispatch }) {
        commit('logout')
        commit('clearProfileTimer')
        await dispatch('permissions/clearMap', null, { root: true })
        await dispatch('company/clearCompanyData', null, { root: true })
        localStorage.removeItem('company')
        await router.push({ name: 'login' })
    },
}